import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';

const initialFormData = {
  travellerName: '',
  email: '',
  phone: '',
  notes: '',
  packageName: '',
  packageDuration: '',
  amount: '',
  gst: '',
  quadMembers: 0,
  quadPrice: '',
  tripleMembers: 0,
  triplePrice: '',
  doubleMembers: 0,
  doublePrice: '',
  location: '',
  numberOfTravellers: 1,
  children: 0,
  includeChildrenInTotal: false,
  travelStartDate: '',
  travelEndDate: '',
  tourType: 'Group',
  departure: '',
  identityType: '',
  identityNumber: '',
  paymentAmount: '',
  transactionId: '',
  paymentDateTime: '',
  paymentMode: 'UPI',
  paymentRemarks: '',
  paymentStatus: 'pending',
  bookingStatus: 'pending',
  emergencyName: '',
  emergencyPhone: ''
};

const Field = ({ label, required, children }) => (
  <div className="min-w-0">
    <label className="block text-[11px] font-semibold uppercase text-slate-500 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const Section = ({ icon, title, children }) => (
  <section className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden ring-1 ring-slate-100">
    <div className="flex items-center gap-2.5 border-b border-slate-200 px-4 py-3 bg-slate-100/80">
      <div className="h-7 w-7 rounded-md bg-white text-[#009ef7] flex items-center justify-center shadow-sm">
        <i className={`bi ${icon} text-xs`}></i>
      </div>
      <h3 className="text-sm font-semibold text-slate-900 m-0">{title}</h3>
    </div>
    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {children}
    </div>
  </section>
);

const inputClass = 'w-full h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#009ef7]/25 focus:border-[#009ef7]';

const identityTypes = ['Aadhaar Card', 'Passport', 'PAN Card', 'Voter ID', 'Driving License'];
const durationOptions = [
  '1 Day',
  '2 Days / 1 Night',
  '3 Days / 2 Nights',
  '4 Days / 3 Nights',
  '5 Days / 4 Nights',
  '6 Days / 5 Nights',
  '7 Days / 6 Nights',
  '8 Days / 7 Nights',
  '9 Days / 8 Nights',
  '10 Days / 9 Nights',
  '11 Days / 10 Nights',
  '12 Days / 11 Nights',
  '13 Days / 12 Nights',
  '14 Days / 13 Nights',
  '15 Days / 14 Nights',
  '16 Days / 15 Nights',
  '17 Days / 16 Nights',
  '18 Days / 17 Nights',
  '19 Days / 18 Nights',
  '20 Days / 19 Nights',
  '21 Days / 20 Nights',
  '22 Days / 21 Nights',
  '23 Days / 22 Nights',
  '24 Days / 23 Nights',
  '25 Days / 24 Nights',
  '26 Days / 25 Nights',
  '27 Days / 26 Nights',
  '28 Days / 27 Nights',
  '29 Days / 28 Nights',
  '30 Days / 29 Nights'
];
const adultOptions = Array.from({ length: 20 }, (_, index) => index + 1);
const childOptions = Array.from({ length: 16 }, (_, index) => index);
const sharingMemberOptions = Array.from({ length: 41 }, (_, index) => index);

const AddBooking = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormData);
  const [identityProof, setIdentityProof] = useState(null);
  const [identityPreview, setIdentityPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!identityProof || !identityProof.type?.startsWith('image/')) {
      setIdentityPreview('');
      return undefined;
    }

    const previewUrl = URL.createObjectURL(identityProof);
    setIdentityPreview(previewUrl);

    return () => URL.revokeObjectURL(previewUrl);
  }, [identityProof]);

  const amountSummary = useMemo(() => {
    const amountPerPerson = Number(formData.amount || 0);
    const adults = Math.max(Number(formData.numberOfTravellers || 0), 0);
    const children = formData.includeChildrenInTotal ? Math.max(Number(formData.children || 0), 0) : 0;
    const sharing = {
      quad: {
        members: Math.max(Number(formData.quadMembers || 0), 0),
        pricePerPerson: Number(formData.quadPrice || 0)
      },
      triple: {
        members: Math.max(Number(formData.tripleMembers || 0), 0),
        pricePerPerson: Number(formData.triplePrice || 0)
      },
      double: {
        members: Math.max(Number(formData.doubleMembers || 0), 0),
        pricePerPerson: Number(formData.doublePrice || 0)
      }
    };
    const sharingMembers = sharing.quad.members + sharing.triple.members + sharing.double.members;
    const hasSharing = sharingMembers > 0;
    const sharingSubtotal =
      (sharing.quad.members * sharing.quad.pricePerPerson) +
      (sharing.triple.members * sharing.triple.pricePerPerson) +
      (sharing.double.members * sharing.double.pricePerPerson);
    const childSubtotal = children * amountPerPerson;
    const passengerCount = (hasSharing ? sharingMembers : adults) + children;
    const subtotal = (hasSharing ? sharingSubtotal : amountPerPerson * adults) + childSubtotal;
    const gstPercent = Number(formData.gst || 0);
    const gstAmount = subtotal * (gstPercent / 100);
    const total = subtotal + gstAmount;
    const paid = Number(formData.paymentAmount || 0);

    return {
      passengerCount,
      subtotal,
      gstAmount,
      total,
      hasSharing,
      sharingMembers,
      sharing: {
        quad: {
          ...sharing.quad,
          totalAmount: sharing.quad.members * sharing.quad.pricePerPerson
        },
        triple: {
          ...sharing.triple,
          totalAmount: sharing.triple.members * sharing.triple.pricePerPerson
        },
        double: {
          ...sharing.double,
          totalAmount: sharing.double.members * sharing.double.pricePerPerson
        }
      },
      estimatedDue: Math.max(total - paid, 0)
    };
  }, [
    formData.amount,
    formData.numberOfTravellers,
    formData.children,
    formData.includeChildrenInTotal,
    formData.quadMembers,
    formData.quadPrice,
    formData.tripleMembers,
    formData.triplePrice,
    formData.doubleMembers,
    formData.doublePrice,
    formData.gst,
    formData.paymentAmount
  ]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const appendJson = (data, key, value) => {
    data.append(key, JSON.stringify(value));
  };

  const buildPayload = () => {
    const data = new FormData();

    [
      'travellerName',
      'email',
      'phone',
      'notes',
      'location',
      'numberOfTravellers',
      'children',
      'includeChildrenInTotal',
      'travelStartDate',
      'travelEndDate',
      'tourType',
      'departure',
      'identityType',
      'identityNumber',
      'paymentStatus',
      'bookingStatus'
    ].forEach((key) => data.append(key, formData[key] || ''));

    appendJson(data, 'packageDetail', {
      name: formData.packageName,
      duration: formData.packageDuration,
      amount: Number(formData.amount || 0),
      gst: Number(formData.gst || 0),
      sharing: {
        enabled: amountSummary.hasSharing,
        quad: amountSummary.sharing.quad,
        triple: amountSummary.sharing.triple,
        double: amountSummary.sharing.double
      },
      totalAmount: amountSummary.total
    });

    appendJson(data, 'payments', Number(formData.paymentAmount) > 0 ? [{
      amount: Number(formData.paymentAmount),
      transactionId: formData.transactionId,
      paymentMode: formData.paymentMode,
      remarks: formData.paymentRemarks,
      paidAt: formData.paymentDateTime || undefined
    }] : []);

    appendJson(data, 'emergencyContact', {
      name: formData.emergencyName,
      phone: formData.emergencyPhone
    });

    if (identityProof) {
      data.append('identityProof', identityProof);
    }

    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      const response = await apiService.postFormData('createBooking', buildPayload());

      if (response.status) {
        toast.success(response.message || 'Booking created successfully');
        navigate('/view-bookings');
      } else {
        toast.error(response.message || 'Failed to create booking');
      }
    } catch (error) {
      console.error('Booking create error:', error);
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-0 space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm ring-1 ring-slate-100 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <nav className="flex flex-wrap items-center text-xs sm:text-sm">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700 no-underline">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/view-bookings" className="text-gray-500 hover:text-gray-700 no-underline">Booking Manage</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Add Booking</span>
        </nav>
        <div className="inline-flex items-center gap-2 rounded-md border border-[#bde7ff] bg-[#f3fbff] px-3 py-2 w-fit">
          <i className="bi bi-receipt text-[#009ef7] text-sm"></i>
          <div className="text-xs font-medium text-slate-700">Invoice auto-generates with <span className="font-semibold text-slate-900">TL-</span></div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Section icon="bi-person-vcard" title="Traveller Details">
          <Field label="Traveller Name" required>
            <input type="text" name="travellerName" value={formData.travellerName} onChange={handleInputChange} className={inputClass} required />
          </Field>
          <Field label="Email" required>
            <input type="email" name="email" value={formData.email} onChange={handleInputChange} className={inputClass} required />
          </Field>
          <Field label="Phone" required>
            <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} className={inputClass} required />
          </Field>
          <Field label="Identity Type" required>
            <select name="identityType" value={formData.identityType} onChange={handleInputChange} className={inputClass} required>
              <option value="">Select identity type</option>
              {identityTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </Field>
          <Field label="Identity Number" required>
            <input type="text" name="identityNumber" value={formData.identityNumber} onChange={handleInputChange} className={inputClass} required />
          </Field>
          <Field label="Identity Document">
            <div className="space-y-2">
              <label className="flex h-10 cursor-pointer items-center justify-between gap-3 rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 text-sm text-slate-600 hover:border-[#009ef7] hover:bg-[#f3fbff]">
                <span className="truncate">{identityProof ? identityProof.name : 'Choose PDF or image file'}</span>
                <i className="bi bi-upload text-[#009ef7]"></i>
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => setIdentityProof(e.target.files?.[0] || null)}
                />
              </label>
              {identityProof && (
                <div className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white p-1.5 shadow-sm max-w-full">
                  {identityPreview ? (
                    <img src={identityPreview} alt="Identity preview" className="max-w-[96px] max-h-16 object-contain rounded border border-slate-100" />
                  ) : (
                    <div className="h-12 w-12 rounded bg-red-50 text-red-600 flex items-center justify-center">
                      <i className="bi bi-file-earmark-pdf text-lg"></i>
                    </div>
                  )}
                  <span className="text-xs text-slate-600 truncate max-w-[160px]">{identityProof.name}</span>
                </div>
              )}
            </div>
          </Field>
        </Section>

        <Section icon="bi-suitcase2" title="Trip And Package">
          <Field label="Package Name">
            <input type="text" name="packageName" value={formData.packageName} onChange={handleInputChange} className={inputClass} />
          </Field>
          <Field label="Duration">
            <select name="packageDuration" value={formData.packageDuration} onChange={handleInputChange} className={inputClass}>
              <option value="">Select duration</option>
              {durationOptions.map((duration) => (
                <option key={duration} value={duration}>{duration}</option>
              ))}
            </select>
          </Field>
          <Field label="Location">
            <input type="text" name="location" value={formData.location} onChange={handleInputChange} className={inputClass} />
          </Field>
          <Field label="Departure">
            <input type="text" name="departure" value={formData.departure} onChange={handleInputChange} className={inputClass} />
          </Field>
          <Field label="Travel Start Date">
            <input type="date" name="travelStartDate" value={formData.travelStartDate} onChange={handleInputChange} className={inputClass} />
          </Field>
          <Field label="Travel End Date">
            <input type="date" name="travelEndDate" value={formData.travelEndDate} onChange={handleInputChange} className={inputClass} />
          </Field>
          <Field label="Adults">
            <select name="numberOfTravellers" value={formData.numberOfTravellers} onChange={handleInputChange} className={inputClass}>
              {adultOptions.map((count) => (
                <option key={count} value={count}>{count}</option>
              ))}
            </select>
          </Field>
          <Field label="Children">
            <select name="children" value={formData.children} onChange={handleInputChange} className={inputClass}>
              {childOptions.map((count) => (
                <option key={count} value={count}>{count}</option>
              ))}
            </select>
          </Field>
          <div className="flex items-end">
            <label className="flex h-10 w-full items-center gap-2 rounded-md border border-slate-300 bg-slate-50 px-3 text-sm text-slate-700">
              <input
                type="checkbox"
                name="includeChildrenInTotal"
                checked={formData.includeChildrenInTotal}
                onChange={handleInputChange}
                className="h-4 w-4 rounded border-slate-300 text-[#009ef7] focus:ring-[#009ef7]"
              />
              Include child in total
            </label>
          </div>
          <Field label="Tour Type">
            <select name="tourType" value={formData.tourType} onChange={handleInputChange} className={inputClass}>
              <option value="Group">Group</option>
              <option value="Private">Private</option>
              <option value="Customized">Customized</option>
            </select>
          </Field>
          <Field label="Amount Per Person">
            <input type="number" name="amount" min="0" value={formData.amount} onChange={handleInputChange} className={inputClass} />
          </Field>
          <Field label="GST (%)">
            <input type="number" name="gst" min="0" value={formData.gst} onChange={handleInputChange} className={inputClass} />
          </Field>
          <Field label="Quad Members">
            <select name="quadMembers" value={formData.quadMembers} onChange={handleInputChange} className={inputClass}>
              {sharingMemberOptions.map((count) => (
                <option key={count} value={count}>{count}</option>
              ))}
            </select>
          </Field>
          <Field label="Quad Price / Person">
            <input type="number" name="quadPrice" min="0" value={formData.quadPrice} onChange={handleInputChange} className={inputClass} />
          </Field>
          <Field label="Triple Members">
            <select name="tripleMembers" value={formData.tripleMembers} onChange={handleInputChange} className={inputClass}>
              {sharingMemberOptions.map((count) => (
                <option key={count} value={count}>{count}</option>
              ))}
            </select>
          </Field>
          <Field label="Triple Price / Person">
            <input type="number" name="triplePrice" min="0" value={formData.triplePrice} onChange={handleInputChange} className={inputClass} />
          </Field>
          <Field label="Double Members">
            <select name="doubleMembers" value={formData.doubleMembers} onChange={handleInputChange} className={inputClass}>
              {sharingMemberOptions.map((count) => (
                <option key={count} value={count}>{count}</option>
              ))}
            </select>
          </Field>
          <Field label="Double Price / Person">
            <input type="number" name="doublePrice" min="0" value={formData.doublePrice} onChange={handleInputChange} className={inputClass} />
          </Field>
          <Field label="Total Amount">
            <input type="number" name="totalAmount" min="0" value={amountSummary.total.toFixed(2)} className={`${inputClass} bg-slate-100 font-semibold`} readOnly />
          </Field>
          <div className="rounded-md border border-[#bde7ff] bg-[#f3fbff] px-3 py-2 text-xs text-slate-700 xl:col-span-3">
            <div className="font-semibold text-slate-900">Calculation</div>
            {amountSummary.hasSharing ? (
              <>
                <div>Quad: {amountSummary.sharing.quad.members} x Rs. {amountSummary.sharing.quad.pricePerPerson.toLocaleString('en-IN')} = Rs. {amountSummary.sharing.quad.totalAmount.toLocaleString('en-IN')}</div>
                <div>Triple: {amountSummary.sharing.triple.members} x Rs. {amountSummary.sharing.triple.pricePerPerson.toLocaleString('en-IN')} = Rs. {amountSummary.sharing.triple.totalAmount.toLocaleString('en-IN')}</div>
                <div>Double: {amountSummary.sharing.double.members} x Rs. {amountSummary.sharing.double.pricePerPerson.toLocaleString('en-IN')} = Rs. {amountSummary.sharing.double.totalAmount.toLocaleString('en-IN')}</div>
                {amountSummary.sharingMembers !== Number(formData.numberOfTravellers || 0) && (
                  <div className="mt-1 font-semibold text-amber-700">Sharing members should match adults: {amountSummary.sharingMembers}/{formData.numberOfTravellers}</div>
                )}
              </>
            ) : (
              <div>{amountSummary.passengerCount} passenger(s) x Rs. {Number(formData.amount || 0).toLocaleString('en-IN')} = Rs. {amountSummary.subtotal.toLocaleString('en-IN')}</div>
            )}
            <div>GST: Rs. {amountSummary.gstAmount.toLocaleString('en-IN')}</div>
          </div>
        </Section>

        <Section icon="bi-credit-card" title="Payment Details">
          <Field label="Paid Amount">
            <input type="number" name="paymentAmount" min="0" value={formData.paymentAmount} onChange={handleInputChange} className={inputClass} />
          </Field>
          <Field label="Transaction ID">
            <input type="text" name="transactionId" value={formData.transactionId} onChange={handleInputChange} className={inputClass} />
          </Field>
          <Field label="Payment Date Time">
            <input type="datetime-local" name="paymentDateTime" value={formData.paymentDateTime} onChange={handleInputChange} className={inputClass} />
          </Field>
          <Field label="Payment Mode">
            <select name="paymentMode" value={formData.paymentMode} onChange={handleInputChange} className={inputClass}>
              <option value="UPI">UPI</option>
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="Cash">Cash</option>
              <option value="Card">Card</option>
            </select>
          </Field>
          <Field label="Payment Status">
            <select name="paymentStatus" value={formData.paymentStatus} onChange={handleInputChange} className={inputClass}>
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
              <option value="refunded">Refunded</option>
            </select>
          </Field>
          <Field label="Booking Status">
            <select name="bookingStatus" value={formData.bookingStatus} onChange={handleInputChange} className={inputClass}>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
          </Field>
          <Field label="Payment Remarks">
            <input type="text" name="paymentRemarks" value={formData.paymentRemarks} onChange={handleInputChange} className={inputClass} />
          </Field>
          <div className="rounded-md bg-[#0f172a] px-3 py-2.5 text-white h-16 flex flex-col justify-center shadow-sm">
            <div className="text-[11px] uppercase text-slate-300">Estimated Due</div>
            <div className="text-lg font-semibold">Rs. {amountSummary.estimatedDue.toLocaleString('en-IN')}</div>
          </div>
        </Section>

        <Section icon="bi-telephone-plus" title="Emergency And Notes">
          <Field label="Emergency Name">
            <input type="text" name="emergencyName" value={formData.emergencyName} onChange={handleInputChange} className={inputClass} />
          </Field>
          <Field label="Emergency Phone">
            <input type="text" name="emergencyPhone" value={formData.emergencyPhone} onChange={handleInputChange} className={inputClass} />
          </Field>
          <div className="sm:col-span-2 xl:col-span-4">
            <Field label="Notes">
              <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows="3" className={`${inputClass} h-auto py-2`} />
            </Field>
          </div>
        </Section>

        <div className="sticky bottom-0 z-10 rounded-lg border border-slate-200 bg-white/95 backdrop-blur px-4 py-3 shadow-lg flex flex-col sm:flex-row justify-end gap-2.5">
          <button type="button" onClick={() => navigate('/view-bookings')} className="h-10 px-5 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200">
            Cancel
          </button>
          <button type="submit" disabled={submitting} className="h-10 px-5 text-sm font-semibold text-white rounded-md disabled:opacity-50 inline-flex items-center justify-center gap-2" style={{ backgroundColor: '#009ef7' }}>
            {submitting && <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>}
            {submitting ? 'Please wait...' : 'Save Booking'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddBooking;
